import React, { useState } from 'react';
import Table from '../../components/common/Table';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { ExerciseTargetDTO } from '../../services/apiClient';
import { renderBooleanBadge } from '../../utils/displayUtils';
import { Link } from 'lucide-react';
import FileIcons from '../common/FileIcons';

interface ExerciseTargetTableProps {
  data: any[];
  handleEdit: (service: any) => void;
  handleDelete: (service: any) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const ExerciseTargetTable: React.FC<ExerciseTargetTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const handleExternalLinkClick = async (url: string) => {
    if (!url) return;
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening link:', error);
    }
  };

  return (
    <Table
      columns={[
        {
          label: 'Title',
          key: 'title',
        },
        {
          label: 'Description',
          key: 'description',
        },
        {
          label: 'Code',
          key: 'code',
        },
        {
          label: 'Target',
          key: 'targetImageId',
          render: (row: ExerciseTargetDTO) => (
            <FileIcons
              files={[
                {
                  id: row.targetImageId,
                  label: 'Target Image',
                },
              ]}
            />
          ),
        },
        {
          label: 'BW',
          key: 'targetImageBwId',
          render: (row: ExerciseTargetDTO) => (
            <FileIcons
              files={[
                {
                  id: row.targetImageBwId,
                  label: 'BW Target Image',
                },
              ]}
            />
          ),
        },
        {
          label: 'Rules',
          key: 'rulesUrl',
          render: (row: ExerciseTargetDTO) =>
            row.rulesUrl ? (
              <button
                onClick={() => handleExternalLinkClick(row.rulesUrl!)}
                className="inline-flex items-center text-primary hover:text-secondary cursor-pointer"
              >
                <Link className="w-4 h-4 mr-1" />
              </button>
            ) : (
              <span className="inline-flex px-2 text-xs font-medium leading-5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                None
              </span>
            ),
        },
        {
          label: 'Sport',
          key: 'evaluationSportImageId',
          render: (row: ExerciseTargetDTO) => (
            <FileIcons
              files={[
                {
                  id: row.evaluationSportImageId,
                  label: 'Sport Evaluation',
                },
              ]}
            />
          ),
        },
        {
          label: 'Hunt',
          key: 'evaluationHuntImageId',
          render: (row: ExerciseTargetDTO) => (
            <FileIcons
              files={[
                {
                  id: row.evaluationHuntImageId,
                  label: 'Hunt Evaluation',
                },
              ]}
            />
          ),
        },
        {
          label: 'Competition',
          key: 'competitionImageId',
          render: (row: ExerciseTargetDTO) => (
            <FileIcons
              files={[
                {
                  id: row.competitionImageId,
                  label: 'Competition Image',
                },
              ]}
            />
          ),
        },
        {
          label: 'Rules',
          key: 'competitionRulesUrl',
          render: (row: ExerciseTargetDTO) =>
            row.competitionRulesUrl ? (
              <button
                onClick={() => handleExternalLinkClick(row.competitionRulesUrl!)}
                className="inline-flex items-center text-primary hover:text-secondary cursor-pointer"
              >
                <Link className="w-4 h-4 mr-1" />
              </button>
            ) : (
              <span className="inline-flex px-2 text-xs font-medium leading-5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                None
              </span>
            ),
        },
        {
          label: 'Position',
          key: 'isPortrait',
          render: (row: ExerciseTargetDTO) =>
            renderBooleanBadge(row.isPortrait ?? false, 'Portrait', 'Landscape'),
        },
        {
          label: 'Status',
          key: 'isActive',
          render: (row: ExerciseTargetDTO) => renderBooleanBadge(row.isActive ?? false),
        },
        {
          label: 'Added',
          key: 'createdUtc',
          format: (createdUtc: Date) => convertToUserTime(createdUtc?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default ExerciseTargetTable;
